import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Autocomplete, TextField, CircularProgress, Typography, Tooltip, Avatar,
} from '@mui/material'
import { SupportAgent as HelperIcon, Edit as EditIcon } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { usersAdminApi, AdminUser } from '../../lib/api/usersAdminApi'
import { usePermission } from '../../lib/hooks/usePermission'
import { authStore } from '../../lib/stores/authStore'
import { themeColors } from '../../app/layout/theme'

interface HelpAssigneeSelectorProps {
  currentPath: string
  lobId?: string | null
  currentHelpAssigneeId?: string | null
  currentHelpAssigneeName?: string | null
  onUpdate: (newHelpAssigneeId: string | null) => Promise<any>
  size?: 'small' | 'medium'
  disabled?: boolean
}

export const HelpAssigneeSelector: React.FC<HelpAssigneeSelectorProps> = ({
  currentPath,
  lobId,
  currentHelpAssigneeId,
  currentHelpAssigneeName,
  onUpdate,
  size = 'small',
  disabled = false,
}) => {
  const { hasAction, isSuperAdmin } = usePermission()

  // Check if the logged-in user IS the current Help Assignee on this record
  const loggedInUserId = authStore.user?.userId
  const isCurrentHelper = !!(loggedInUserId && currentHelpAssigneeId && loggedInUserId.toLowerCase() === currentHelpAssigneeId.toLowerCase())

  // Help Assignee cannot reassign even if they have the permission
  const hasPermission = isSuperAdmin || hasAction(currentPath, 'addhelpassignee')
  const canEdit = hasPermission && !isCurrentHelper && !disabled

  const [open, setOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Fetch eligible users (LOB team members + Org/Super Admins)
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['help-assignee-users', lobId],
    queryFn: () => usersAdminApi.getAll({ lobId: lobId || undefined, pageSize: 200 }),
    enabled: open && canEdit,
  })

  const users = usersData?.items || []

  const handleOpen = () => {
    if (!canEdit) return
    // Pre-select current user if matching
    const current = users.find(u => u.id === currentHelpAssigneeId) || null
    setSelectedUser(current)
    setOpen(true)
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const newId = selectedUser ? selectedUser.id : null
      await onUpdate(newId)
      toast.success(newId ? 'Help Assignee updated successfully' : 'Help Assignee removed')
      setOpen(false)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update Help Assignee')
    } finally {
      setIsSaving(false)
    }
  }

  // If user has no permission and no helper is assigned, hide entirely
  if (!hasPermission && !currentHelpAssigneeName && !isCurrentHelper) {
    return null
  }

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
      <Typography fontSize="11px" color="text.secondary" mb={0.75} fontWeight={500}>
        Help Assignee
      </Typography>

      {/* Case 1: Current user IS the Help Assignee — show "You are the Help Assignee" */}
      {isCurrentHelper ? (
        <Tooltip title="You are assigned as the Help Assignee for this record">
          <Chip
            // icon={<HelperIcon style={{ fontSize: 16 }} />}
            label="You are the Help Assignee"
            variant="filled"
            color="primary"
            size={size}
            sx={{
              cursor: 'default',
              fontWeight: 500,
              borderRadius: '8px',
            }}
          />
        </Tooltip>
      ) : canEdit ? (
        /* Case 2: User has permission and is NOT the helper — show editable chip */
        <>
          <Tooltip title="Click to change Help Assignee">
            <Chip
              label={
                currentHelpAssigneeName ? (
                  <span>
                    <strong>Helper:</strong> {currentHelpAssigneeName}
                  </span>
                ) : (
                  <span style={{ opacity: 0.7 }}>+ Add Helper</span>
                )
              }
              variant={currentHelpAssigneeName ? 'filled' : 'outlined'}
              color={currentHelpAssigneeName ? 'info' : 'default'}
              size={size}
              onClick={handleOpen}
              onDelete={currentHelpAssigneeName ? handleOpen : undefined}
              deleteIcon={<EditIcon style={{ fontSize: 14 }} />}
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                borderRadius: '16px',
                '&:hover': { filter: 'brightness(0.95)' },
              }}
            />
          </Tooltip>

          {open && (
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Select Help Assignee
              </DialogTitle>

              <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose an active team member from this LOB or an Org Admin to assist on this record.
                </Typography>

                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <Autocomplete
                    options={users}
                    getOptionLabel={(u) => u.name + (u.email ? ` (${u.email})` : '')}
                    value={selectedUser}
                    onChange={(_, newValue) => setSelectedUser(newValue)}
                    renderOption={(props, u) => (
                      <Box component="li" {...props} key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: `${themeColors.primary}` }}>
                          {u.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                            {u.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {u.email} {u.lobName ? `• ${u.lobName}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Help Assignee"
                        placeholder="Type name or email..."
                        fullWidth
                        autoFocus
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    clearOnEscape
                  />
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2 }}>
                {currentHelpAssigneeId && (
                  <Button
                    color="error"
                    onClick={() => setSelectedUser(null)}
                    disabled={isSaving}
                    sx={{ mr: 'auto' }}
                  >
                    Clear Helper
                  </Button>
                )}
                <Button onClick={() => setOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      ) : currentHelpAssigneeName ? (
        /* Case 3: No permission, not the helper, but a helper exists — show read-only */
        <Chip
          label={
            <span>
              <strong>Helper:</strong> {currentHelpAssigneeName}
            </span>
          }
          variant="filled"
          color="info"
          size={size}
          sx={{
            cursor: 'default',
            fontWeight: 500,
            borderRadius: '16px',
          }}
        />
      ) : null}
    </Box>
  )
}

